import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import Router from 'next/router'
import React, { useCallback, useEffect, useMemo } from 'react'
import { resolveOnDemandCity } from 'src/constants/onDemandCoverage'
import {
  PILLAR_NS_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SearchPageCity
} from 'src/constants/searchPages'
import useLastSearch from 'src/hooks/useLastSearch'
import usePillarNames from 'src/hooks/usePillarNames'
import ga from 'src/utils/pixels/gtag'
import { buildWhatsappUrl } from 'src/utils/whatsapp'
import {
  IGoogleAutocompleteSuggestion,
  IGooglePlaceDetails,
  SizeFilter
} from '../../types/graphql'
import { RedirectUrls } from '../../types/types'
import {
  HERO_PRIMARY_CTA_CLASS,
  HERO_SIBLING_CHIP_CLASS,
  HeroImagePreload,
  HeroMobileBand,
  HeroPhotoCard
} from './HeroMasthead'

const B2B_PILLARS: ReadonlySet<PillarPage> = new Set([
  PillarPage.Warehouse,
  PillarPage.Industrial
])

const CustomAutoComplete = dynamic(
  () => import('../../components/AutoComplete/LandingSearch')
)

// Escape `< > & "` so an i18n value containing an angle bracket can never
// break out of the dangerouslySetInnerHTML payload below. Defensive — locale
// JSON in this repo is author-controlled, but cheap insurance. See CLAUDE.md
// "Trigger H — FB IAB strips React `<!-- -->` boundary markers".
const escapeForHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

interface IProps {
  pillar: PillarPage
  ctaHref: string
  /** Resolved SearchPageCity (geo, IP, or Monterrey fallback). Needed for
   *  the size-chip + on-demand-chip routing on the SelfStorage pillar. */
  city: SearchPageCity
  /** Display name of the resolved city (geo or IP). Empty when unresolved. */
  cityName?: string
  /** True only when geo/IP have resolved a city — never on the SSR pass and
   *  never on the Monterrey fallback. Gates all post-hydration personalization
   *  so crawlers index a clean Mexico-wide hero. */
  cityKnown?: boolean
}

/**
 * Generic / SEO-facing pillar landing hero.
 *
 * This is the hero served on SSR and to organic traffic — anyone arriving
 * without a `?city=` URL param. The SSR HTML is always Mexico-wide so it
 * ranks for broad pillar queries.
 *
 * Per-pillar CTA shape mirrors {@link HeroPersonalized} intent:
 *
 *   - SelfStorage / Parking (B2C): primary "Ver listings", no secondary.
 *     SelfStorage adds size chips (XS/S/M/L) and the on-demand chip when
 *     the resolved city has a partner-active market.
 *   - Warehouse / Industrial (B2B): WhatsApp specialist is the PRIMARY —
 *     B2B closes through the specialist, not self-serve listings. Listings
 *     becomes the outlined secondary.
 *
 * The H1, subhead, and stats stay generic to avoid CLS shift and keep
 * crawlers happy. Post-hydration, when geo/IP resolve a city, the CTA copy
 * + chip rail layer on. Paid traffic with `?city=<slug>` swaps to
 * {@link HeroPersonalized} entirely.
 */
const Hero = ({ pillar, ctaHref, city, cityName, cityKnown }: IProps) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tCommon } = useTranslation('common')
  const { t: tStats } = useTranslation('platform_stats')
  const { saveLastSearch } = useLastSearch()
  // Locale-aware breadcrumb label — PILLAR_PAGE_MAP.label is Spanish-only.
  const { label: pillarLabel } = usePillarNames(pillar)
  const isB2B = B2B_PILLARS.has(pillar)

  const VALUE_PROPS = [
    { key: 'flexible', text: t('HERO.VALUE_PROPS.FLEXIBLE') },
    { key: 'secure', text: t('HERO.VALUE_PROPS.SECURE') }
  ]

  const STATS = [
    { value: tStats('SPACES'), label: tStats('SPACES_LABEL') },
    { value: tStats('CITIES'), label: tStats('CITIES_LABEL') }
    // No RATING (no real reviews) and no USERS ("40,000+" counts visitors,
    // not users) — 2026-09-30.
  ]

  // Keep the action short; a reserved line directly above it names the city.
  const listingsLabel = t('HERO.PRIMARY_CTA')

  // Per-pillar CTA shape:
  //   B2C → primary = listings (loud blue pill)
  //   B2B → primary = WhatsApp specialist (loud blue pill), secondary = listings (outlined)
  // B2B inverts the listings/specialist hierarchy because the actual deal
  // closes via the specialist (off-platform Anexo C/B flow), not self-serve.
  const primaryAction = useMemo(() => {
    if (isB2B) {
      const message =
        cityKnown && cityName
          ? t('HERO.WHATSAPP_MSG_CITY', { city: cityName })
          : t('HERO.WHATSAPP_MSG')
      return {
        href: buildWhatsappUrl(message),
        label: t('HERO.BROKER_CTA'),
        gaLabel: 'broker_cta_wa',
        external: true
      }
    }
    return {
      href: ctaHref,
      label: listingsLabel,
      gaLabel: 'view_listings',
      external: false
    }
  }, [isB2B, cityKnown, cityName, t, ctaHref, listingsLabel])

  const secondaryAction = isB2B
    ? {
        href: ctaHref,
        // No-city label keeps the button from wrapping on mobile beside
        // the WA primary. H1 already names the resolved city when known.
        label: t('HERO.PRIMARY_CTA'),
        gaLabel: 'view_listings_secondary'
      }
    : null

  // SelfStorage size chips — quick-jump by size filter. Only show when
  // cityKnown so the chip href has a valid city to anchor on.
  const SIZE_CHIPS = useMemo(
    () => [
      {
        key: 'XSMALL' as const,
        label: t('HERO.SIZE_CHIPS.XSMALL'),
        filter: SizeFilter.Xsmall
      },
      {
        key: 'SMALL' as const,
        label: t('HERO.SIZE_CHIPS.SMALL'),
        filter: SizeFilter.Small
      },
      {
        key: 'MEDIUM' as const,
        label: t('HERO.SIZE_CHIPS.MEDIUM'),
        filter: SizeFilter.Medium
      },
      {
        key: 'LARGE' as const,
        label: t('HERO.SIZE_CHIPS.LARGE'),
        filter: SizeFilter.Large
      }
    ],
    [t]
  )
  const showSizeChips = pillar === PillarPage.SelfStorage && cityKnown
  const buildChipHref = useCallback(
    (filter: SizeFilter): string => `/${pillar}/${city}?sizes=${filter}`,
    [city, pillar]
  )

  // On-demand chip — SelfStorage pillar only. ALWAYS shown on storage
  // (matches home hero behaviour), with the href adapting based on partner
  // coverage: city-specific page when the resolved city has an active
  // partner, generic landing otherwise. Pre-2026-05-20 this was double-
  // gated on cityKnown + partnerActive — that meant organic visitors
  // arriving without resolved geo (the common case) saw nothing, even
  // though there's a perfectly good fallback landing page that exists.
  const onDemand = resolveOnDemandCity(city)
  const showOnDemandChip = pillar === PillarPage.SelfStorage
  const onDemandHref = onDemand?.partnerActive
    ? `/renta-mini-bodegas/almacenamiento-a-domicilio/${onDemand.city}`
    : `/renta-mini-bodegas/almacenamiento-a-domicilio`

  // Flotillas sibling-product chip — Parking pillar only. Parking has no
  // per-vehicle search filter (every vehicle card maps to the single PARKING
  // size bucket), so the one chip that leads somewhere genuinely different is
  // the B2B fleet page — the same "Para flotilla" chip the home hero carries.
  // Href comes from the pillar config's crossLink so the two never drift.
  const fleetHref = PILLAR_PAGE_MAP[pillar].crossLink?.href
  const showFleetChip = pillar === PillarPage.Parking && !!fleetHref

  useEffect(() => {
    Router.prefetch(ctaHref)
  }, [ctaHref])

  // `city` on organic fires is gated on cityKnown — the Monterrey fallback
  // is a default, not a resolved city, and would read as real geo in PostHog.
  const cityParam = useMemo(
    () => (cityKnown ? { city } : {}),
    [cityKnown, city]
  )

  const handlePrimaryClick = useCallback(() => {
    // B2C primary routes to the listings page — save lastSearch so the
    // home LastSearchCard can resume the visitor next time they land.
    // B2B primary opens WhatsApp (`external`) — no resume semantics, so
    // skip the save there.
    if (!primaryAction.external) {
      saveLastSearch({ pillar, city })
    }
    ga.event('hero_cta_click', {
      pillar,
      ...cityParam,
      source: 'primary',
      cta: primaryAction.gaLabel
    })
  }, [
    pillar,
    city,
    cityParam,
    primaryAction.gaLabel,
    primaryAction.external,
    saveLastSearch
  ])

  const handleSecondaryClick = useCallback(() => {
    if (!secondaryAction) return
    // B2B secondary IS the listings link — save lastSearch here too so
    // the resume path covers the click even when the primary was WA.
    saveLastSearch({ pillar, city })
    ga.event('hero_cta_click', {
      pillar,
      ...cityParam,
      source: 'secondary',
      cta: secondaryAction.gaLabel
    })
  }, [pillar, city, cityParam, secondaryAction, saveLastSearch])

  const handleOnDemandChipClick = useCallback(() => {
    ga.event('hero_cta_click', {
      pillar,
      ...cityParam,
      source: 'on_demand_chip',
      cta: 'on_demand_chip'
    })
  }, [pillar, cityParam])

  const handleFleetChipClick = useCallback(() => {
    ga.event('hero_cta_click', {
      pillar,
      ...cityParam,
      source: 'fleet_chip',
      cta: 'fleet_chip'
    })
  }, [pillar, cityParam])

  // Size chips + size-guide link — `cta:'size_chip'` / `cta:'size_guide'`
  // is the cross-variant filter key (the paid HeroPersonalized fires the
  // same `cta` values with source:'url'), `size_filter` carries the bucket.
  // NOT named `size`: a property called `size` collides with the ClickHouse
  // function of the same name and makes the event un-queryable in HogQL.
  const handleSizeChipClick = useCallback(
    (filter: SizeFilter) => {
      ga.event('hero_cta_click', {
        pillar,
        ...cityParam,
        source: 'size_chip',
        cta: 'size_chip',
        size_filter: filter
      })
    },
    [pillar, cityParam]
  )

  const handleSizeGuideClick = useCallback(() => {
    ga.event('hero_cta_click', {
      pillar,
      ...cityParam,
      source: 'size_guide_link',
      cta: 'size_guide'
    })
  }, [pillar, cityParam])

  const handleOnSearch = useCallback(
    (
      place: IGoogleAutocompleteSuggestion | undefined,
      search: string,
      coords?: IGooglePlaceDetails | undefined
    ) => {
      const { description, placeId } = place ?? {}
      const { query } = PILLAR_PAGE_MAP[pillar]
      ga.event('search', { search, description })
      ga.event('hero_cta_click', {
        pillar,
        ...cityParam,
        source: 'search_fallback'
      })
      Router.push({
        pathname: RedirectUrls.Search,
        query: {
          description,
          placeId,
          ...query,
          ...(coords?.lat && coords?.lng
            ? { lat: coords?.lat, lng: coords?.lng }
            : {})
        }
      })
    },
    [pillar, cityParam]
  )

  const liveBadge = tCommon('PILLAR_SECTIONS.LIVE_BADGE')

  const statsRow = STATS.map((stat) => (
    <div key={stat.label} className="flex min-w-0 flex-col gap-1">
      {/* Steps down at md: the 5/12 rail is ~92px per tile at 900px and
          "40,000+" overflowed it at text-3xl. */}
      <dt className="font-display text-[clamp(18px,6vw,24px)] leading-none font-medium whitespace-nowrap text-ink-700 md:text-xl lg:text-2xl xl:text-3xl">
        {stat.value}
      </dt>
      <dd className="text-[11px] tracking-[0.08em] text-ink-200 uppercase">
        {stat.label}
      </dd>
    </div>
  ))

  return (
    <section className="relative w-full overflow-hidden bg-cream-300 pt-(--nav-h) pb-14 md:pt-[calc(var(--nav-h)+64px)] md:pb-24">
      <HeroImagePreload pillar={pillar} />
      <div className="pointer-events-none absolute top-20 -right-20 size-64 rounded-full bg-blue-500/6 blur-3xl" />

      {/* Mobile masthead — photo band in flow under the nav (parity with the
          2026-09-05 home hero). Desktop keeps the framed right-rail card. */}
      <HeroMobileBand pillar={pillar} badge={liveBadge} />

      <div className="relative z-10 mx-auto -mt-10 grid max-w-7xl grid-cols-1 items-center gap-10 px-5 md:mt-0 md:grid-cols-12 md:gap-14 md:px-10">
        {/* Left: editorial content rail */}
        <div className="flex flex-col gap-4 md:col-span-7 md:gap-7">
          <p className="font-display text-sm tracking-[0.22em] text-blue-500 uppercase">
            {pillarLabel}
          </p>

          {/* Headline — body weight in Rubik, the emphasis in Fraunces italic.
              dangerouslySetInnerHTML is deliberate — see CLAUDE.md "Trigger H —
              FB IAB strips React `<!-- -->` boundary markers". JSX with
              text→element→text siblings serializes at SSR with React boundary
              comments between each child; FB IAB strips HTML comments, React 19
              strict-compares the byte stream and fires #418. Inlining via
              innerHTML emits a single continuous text node with no boundaries
              for FB IAB to strip. */}
          <h1
            className="max-w-2xl text-[clamp(32px,10vw,40px)] leading-[1.04] font-medium tracking-[-0.015em] break-words text-ink-700 md:text-[64px] lg:text-[72px]"
            dangerouslySetInnerHTML={{
              __html: `${escapeForHtml(t('HERO.TITLE.TEXT_DARK_1'))} <span class="font-display text-[0.96em] font-normal text-blue-500 italic">${escapeForHtml(t('HERO.TITLE.TEXT_BLUE'))}</span> ${escapeForHtml(t('HERO.TITLE.TEXT_DARK_2'))}`
            }}
          />

          <p className="max-w-xl text-base leading-relaxed text-ink-200 md:text-lg">
            <Link
              href="/"
              className="font-medium text-ink-700 underline decoration-blue-500/40 decoration-1 underline-offset-[6px] hover:decoration-blue-500"
            >
              SpotMe
            </Link>
            {` ${t('HERO.SUBTITLE', { cities: tStats('CITIES') })}`}
          </p>

          {/* CTA cluster — per-pillar shape. Chrome is the shared
              HERO_PRIMARY_CTA_CLASS (blue-500 + glow, same as the home hero)
              so the brand entry, the organic hero and the paid hero read as
              one system. Stacks on mobile, sits side-by-side on desktop. */}
          <div className="flex w-full max-w-2xl flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-3">
            <p className="min-h-5 w-full text-sm font-medium text-ink-200">
              {cityKnown && cityName
                ? t('HERO.GEO_PILL_LABEL', { city: cityName })
                : ''}
            </p>
            {primaryAction.external ? (
              <a
                href={primaryAction.href}
                onClick={handlePrimaryClick}
                target="_blank"
                rel="noopener noreferrer"
                className={HERO_PRIMARY_CTA_CLASS}
              >
                <span>{primaryAction.label}</span>
                <ArrowForwardRoundedIcon className="text-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
              </a>
            ) : (
              <Link
                href={primaryAction.href}
                onClick={handlePrimaryClick}
                className={HERO_PRIMARY_CTA_CLASS}
              >
                <span>{primaryAction.label}</span>
                <ArrowForwardRoundedIcon className="text-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            )}
            {secondaryAction && (
              <Link
                href={secondaryAction.href}
                onClick={handleSecondaryClick}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink-100/30 bg-white-500 px-7 py-4 text-center text-base font-medium text-ink-700 transition-all duration-200 hover:border-blue-500 hover:text-blue-500 active:scale-[0.98] md:w-auto md:px-7 md:text-[17px]"
              >
                <span>{secondaryAction.label}</span>
              </Link>
            )}
          </div>

          {/* SelfStorage-only quick-jump size chips. Hidden until cityKnown
              so the chip href always points at a real city listings page. */}
          {showSizeChips && (
            <div className="flex flex-col gap-2">
              <p className="font-display text-[11px] tracking-[0.18em] text-ink-200 uppercase">
                {t('HERO.QUICK_FILTERS_LABEL')}
              </p>
              <ul className="flex flex-wrap gap-2">
                {SIZE_CHIPS.map((chip) => (
                  <li key={chip.key}>
                    <Link
                      href={buildChipHref(chip.filter)}
                      onClick={() => handleSizeChipClick(chip.filter)}
                      className="inline-flex items-center rounded-full border border-ink-100/30 bg-white-500/70 px-3.5 py-1.5 text-[13px] font-medium text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500"
                    >
                      {chip.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* On-demand sibling-product chip — SelfStorage pillar always.
              Href adapts: city-specific page when the resolved city has
              an active partner, generic landing otherwise. Matches the
              home hero's permissive behaviour rather than the strict
              HeroPersonalized gating (paid traffic gating expects a
              URL-confirmed city, organic doesn't have that guarantee).
              Blue-tinted border distinguishes it from the grey size
              chips above. */}
          {showOnDemandChip && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <Link
                href={onDemandHref}
                onClick={handleOnDemandChipClick}
                className={HERO_SIBLING_CHIP_CLASS}
              >
                <i
                  className="si si-delivery-truck text-[13px] text-blue-500"
                  aria-hidden="true"
                />
                {t('HERO.ON_DEMAND_CTA')}
                <i
                  className="si si-right text-[8px] transition-transform duration-200 group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </Link>
              <span className="text-[12px] leading-snug text-ink-200">
                {t('HERO.ON_DEMAND_CTA_HELP')}
              </span>
            </div>
          )}

          {/* Flotillas sibling-product chip — Parking pillar only. */}
          {showFleetChip && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <Link
                href={fleetHref}
                onClick={handleFleetChipClick}
                className={HERO_SIBLING_CHIP_CLASS}
              >
                <i
                  className="si si-delivery-truck text-[13px] text-blue-500"
                  aria-hidden="true"
                />
                {t('HERO.FLEET_CTA')}
                <i
                  className="si si-right text-[8px] transition-transform duration-200 group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </Link>
              <span className="text-[12px] leading-snug text-ink-200">
                {t('HERO.FLEET_CTA_HELP')}
              </span>
            </div>
          )}

          {/* Search remains secondary to the listing action and quick choices. */}
          <div className="w-full max-w-md opacity-60 transition-opacity duration-200 focus-within:opacity-100 hover:opacity-100">
            <p className="mb-2 text-[11px] tracking-[0.06em] text-ink-200 uppercase md:text-xs">
              {t('HERO.AUTOCOMPLETE_HINT')}
            </p>
            <CustomAutoComplete
              handleOnSelect={handleOnSearch}
              placeholder={t('HERO.SEARCH_PHOLDER')}
            />
          </div>

          {/* Value props — minimal inline, no pills */}
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink-200">
            {VALUE_PROPS.map((prop) => (
              <li key={prop.key} className="flex items-center gap-1.5">
                <CheckRoundedIcon className="text-base text-blue-500" />
                <span>{prop.text}</span>
              </li>
            ))}
          </ul>

          {/* SizeGuide tertiary link — SelfStorage only. */}
          {pillar === PillarPage.SelfStorage && (
            <Link
              href={RedirectUrls.SizeGuide}
              onClick={handleSizeGuideClick}
              className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-200 underline decoration-ink-100/40 decoration-1 underline-offset-[5px] transition-colors hover:text-blue-500 hover:decoration-blue-500/50"
            >
              <i className="si si-ruler-combined text-sm" />
              {tCommon('FILTER.SIZE_GUIDE')}
            </Link>
          )}

          {/* Mobile-only stat row — the photo moved into the masthead band,
              so the trust figures close the copy rail instead. */}
          <dl className="grid grid-cols-2 gap-4 border-t border-ink-100/20 pt-5 md:hidden">
            {statsRow}
          </dl>
        </div>

        {/* Right (desktop only): framed square photo + stat caption. Mobile
            gets the photo as the masthead band above instead. */}
        <div className="hidden md:col-span-5 md:block">
          <HeroPhotoCard pillar={pillar} badge={liveBadge} />
          <dl className="mt-6 grid grid-cols-2 gap-4 border-t border-ink-100/20 pt-5">
            {statsRow}
          </dl>
        </div>
      </div>
    </section>
  )
}

export default React.memo(Hero)
